import { queryOptions, useQuery } from "@tanstack/react-query";
import { getAuthorizationHeader } from "../../Auth/index.js";
var CachedQueryKeys = /* @__PURE__ */ ((CachedQueryKeys2) => {
  CachedQueryKeys2["AppConfig"] = "appConfig";
  CachedQueryKeys2["AppTheme"] = "appTheme";
  CachedQueryKeys2["UserCart"] = "userCart";
  CachedQueryKeys2["BrandAppConfigs"] = "brandAppConfigs";
  CachedQueryKeys2["BrandClub"] = "brandClub";
  CachedQueryKeys2["DTCProduct"] = "dtcProduct";
  CachedQueryKeys2["SiteProduct"] = "siteProduct";
  CachedQueryKeys2["UserProfile"] = "userProfile";
  CachedQueryKeys2["ThemeSetup"] = "themeSetup";
  CachedQueryKeys2["SuggestedQueries"] = "suggestedQueries";
  return CachedQueryKeys2;
})(CachedQueryKeys || {});
const THIRTY_MINUTES_IN_MS = 1e3 * 60 * 30;
const FIVE_MINUTES_IN_MS = 1e3 * 60 * 5;
const getHostDomain = () => {
  if (window.location.hostname.includes("brandclub.com")) {
    return window.location.origin;
  }
  const scripts = Array.from(document.getElementsByTagName("script"));
  const checkoutScript = scripts.find((s) => {
    var _a;
    return (_a = s.src) == null ? void 0 : _a.includes("brandclub-checkout-shop-app");
  });
  if (checkoutScript) {
    try {
      return new URL(checkoutScript.src).origin;
    } catch {
    }
  }
  return window.location.origin;
};
const getCommonRequestHeaders = () => {
  return {
    // "X-Client-AppId": ConfigUtils.getWebAppId(), // TODO: Temporarily do not send this header
  };
};
const getAppConfig = async (signal) => {
  const DOMAIN = `${getHostDomain()}/api-rewards`;
  const res = await fetch(DOMAIN + "/config/webApp/getAppConfig", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...getCommonRequestHeaders()
      // Note: getAuthorizationHeader would need to be imported from common-ui
      // For now, we'll assume it's available or handle auth differently
    },
    signal
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error("Error fetching app config");
  }
};
const getBrandAppConfigs = async (signal, apiBaseUrl) => {
  const DOMAIN = apiBaseUrl || `${getHostDomain()}/api-rewards`;
  const res = await fetch(DOMAIN + "/brandapp/brandAppConfig", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...getCommonRequestHeaders(),
      ...await getAuthorizationHeader()
    },
    signal
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error("Error fetching brand app configs");
  }
};
const getSuggestedQueries = async (_signal, brandId, apiBaseUrl) => {
  const DOMAIN = apiBaseUrl || `${getHostDomain()}/api-rewards`;
  const url = new URL(DOMAIN + "/concierge/suggestedQueries");
  if (brandId) {
    url.searchParams.set("brandId", brandId.toString());
  }
  try {
    const authHeaders = await getAuthorizationHeader();
    const res = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getCommonRequestHeaders(),
        ...authHeaders
      }
    });
    if (res.ok) {
      const data = await res.json();
      return data;
    } else {
      console.error("getSuggestedQueries - error response:", res.status, res.statusText);
      throw new Error("Error fetching suggested queries");
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return { suggestedQuestions: [] };
    }
    console.error("getSuggestedQueries - caught error:", error);
    throw error;
  }
};
const brandAppConfigQueryOptions = (apiBaseUrl) => {
  return queryOptions({
    queryKey: ["brandAppConfigs", apiBaseUrl],
    queryFn: ({ signal }) => getBrandAppConfigs(signal, apiBaseUrl),
    // since config data rarely changes, we can use the cached data for longer and refresh it every 30 minutes
    staleTime: THIRTY_MINUTES_IN_MS
  });
};
const appConfigQueryOptions = () => {
  return queryOptions({
    queryKey: [
      "appConfig"
      /* AppConfig */
    ],
    queryFn: ({ signal }) => getAppConfig(signal),
    // since config data rarely changes, we can use the cached data for longer and refresh it every 30 minutes
    staleTime: THIRTY_MINUTES_IN_MS
  });
};
const suggestedQueriesQueryOptions = (brandId, apiBaseUrl) => {
  return queryOptions({
    queryKey: ["suggestedQueries", brandId, apiBaseUrl],
    queryFn: ({ signal }) => getSuggestedQueries(signal, brandId, apiBaseUrl),
    // Suggested queries can be cached for a shorter time since they might change more frequently
    staleTime: FIVE_MINUTES_IN_MS
  });
};
const useAppConfig = (args) => {
  return useQuery({
    ...appConfigQueryOptions(),
    ...args,
    select: args == null ? void 0 : args.select
  });
};
const useDomainConfig = (args) => {
  const { data: appConfig, ...rest } = useAppConfig(args);
  return {
    data: appConfig == null ? void 0 : appConfig.domainConfig,
    ...rest
  };
};
const useBrandAppConfig = (apiBaseUrl, args) => {
  return useQuery({
    ...brandAppConfigQueryOptions(apiBaseUrl),
    ...args,
    select: args == null ? void 0 : args.select
  });
};
const useSuggestedQueries = (brandId, apiBaseUrl, args) => {
  return useQuery({
    ...suggestedQueriesQueryOptions(brandId, apiBaseUrl),
    ...args,
    select: args == null ? void 0 : args.select
  });
};
export {
  CachedQueryKeys,
  FIVE_MINUTES_IN_MS,
  THIRTY_MINUTES_IN_MS,
  appConfigQueryOptions,
  brandAppConfigQueryOptions,
  suggestedQueriesQueryOptions,
  useAppConfig,
  useBrandAppConfig,
  useDomainConfig,
  useSuggestedQueries
};
//# sourceMappingURL=appConfigQueryOptions.js.map
